// src/components/core/entity/EntityChannelsCard/ContactPersonModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import entitiesService from 'services/entitiesService';
import { returnOnlyNumbers } from 'utils/helpers';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ContactPersonSearchContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, goFocus } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch both fields for dynamic required logic (at least one must be filled)
  const watchPhone = Form.useWatch('searchPhone', form);
  const watchEmail = Form.useWatch('searchEmail', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [phoneEmailState, setPhoneEmailState] = useState({
    hasPhone: false,
    hasEmail: false,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-focus the phone field on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      goFocus?.('searchPhone.value', true);
    }, 150);
    return () => clearTimeout(timer);
  }, [goFocus]);

  // Track phone/email values for dynamic required (at least one must be filled)
  useEffect(() => {
    const phoneVal = typeof watchPhone === 'object' ? watchPhone?.value : watchPhone;
    const emailVal = typeof watchEmail === 'object' ? watchEmail?.value : watchEmail;
    setPhoneEmailState({
      hasPhone: !!phoneVal && phoneVal.trim() !== '',
      hasEmail: !!emailVal && emailVal.trim() !== '',
    });
  }, [watchPhone, watchEmail]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Dynamic required: both required when neither has value, optional when the other has value
  const phoneRequired = !phoneEmailState.hasEmail;
  const emailRequired = !phoneEmailState.hasPhone;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Hidden fields for Step2 communication */}
      <Form.Item name="_searchResult" hidden noStyle />
      <Form.Item name="_entityFound" hidden noStyle />

      <SimplePhone
        name={['searchPhone', 'value']}
        label="phone"
        international
        required={phoneRequired}
      />

      <SimpleEmail
        name={['searchEmail', 'value']}
        label="email"
        placeholder="contact@example.com"
        required={emailRequired}
      />
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const contactPersonSearchStep = () => ({
  stepTitle: 'search_contact',
  stepTooltip: 'search_by_email_or_phone',
  content: () => <ContactPersonSearchContent />,
  // No validationFields - validation is handled in onNext (dynamic OR logic)
  nextButtonText: 'search',
  nextButtonIcon: <SearchOutlined />,
  onNext: async ({ form }) => {
    const values = form.getFieldsValue(true);

    // Extract phone value
    const rawPhone = values.searchPhone;
    const phoneValue = typeof rawPhone === 'object' ? rawPhone?.value : rawPhone;
    const cleanPhone = phoneValue ? returnOnlyNumbers(phoneValue) : '';

    // Extract email value
    const rawEmail = values.searchEmail;
    const emailValue = typeof rawEmail === 'object' ? rawEmail?.value : rawEmail;
    const cleanEmail = emailValue?.trim() || '';

    // At least one must be filled
    if (!cleanPhone && !cleanEmail) return false;

    // Search: try phone first (if provided), then email
    let entity = null;

    if (cleanPhone) {
      const result = await entitiesService.getEntitiesByField({
        field: 'value',
        value: cleanPhone,
        path: 'contacts',
        fullSearch: true,
      });
      const results = Array.isArray(result) ? result : result?._id ? [result] : [];
      entity = results.find(e => e.entityType === 'person') || null;
    }

    if (!entity?._id && cleanEmail) {
      const result = await entitiesService.getEntitiesByField({
        field: 'value',
        value: cleanEmail,
        path: 'contacts',
        fullSearch: true,
      });
      const results = Array.isArray(result) ? result : result?._id ? [result] : [];
      entity = results.find(e => e.entityType === 'person') || null;
    }

    if (entity && entity._id) {
      // Found - store result and mark as found
      form.setFieldsValue({
        _searchResult: entity,
        _entityFound: true,
        note: '',
      });
    } else {
      // Not found - clear result and pre-fill Step2 fields with search values
      form.setFieldsValue({
        _searchResult: null,
        _entityFound: false,
        note: '',
      });

      if (cleanPhone) {
        const phoneCC = (typeof rawPhone === 'object' ? rawPhone?.countryCode : null) || 'BR';
        form.setFieldsValue({ personPhone: { value: phoneValue, countryCode: phoneCC } });
      }
      if (cleanEmail) {
        form.setFieldsValue({ personEmail: { value: cleanEmail } });
      }
    }

    return true; // Advance to Step2
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default contactPersonSearchStep;
